import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, Smartphone, Globe, Plus, Share2, FileText, User, BookOpen, Expand } from "lucide-react";
import EnhancedBookCover from "@/components/EnhancedBookCover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EnrichedPublisherBook } from "@/services/publisherService";
import { AppleBook, searchAppleBooks, generateAppleBooksWebUrl, generateAppleBooksDeepLink, canOpenAppleBooksApp } from "@/services/appleBooks";
import { searchGoogleBooks } from "@/services/googleBooks";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { searchFreeEbooks, EbookSearchResult, preferredEbookSource } from "@/services/freeEbookService";
import { gsap } from "gsap";
import { analyticsService } from "@/services/analyticsService";
import { getOptimizedSettings } from "@/utils/performance";
import ShareBookModal from "@/components/ShareBookModal";
import CoverLightbox from "@/components/CoverLightbox";
import { titleMatches, authorMatches } from "../../supabase/functions/_shared/bookCover";
import { fetchVerifiedCovers, coverCacheKey } from "@/services/verifiedCovers";
import ScreenplayReaderModal from "@/components/ScreenplayReaderModal";
import { supabase } from "@/integrations/supabase/client";
import { ScifiAuthor } from "@/services/scifiAuthorsService";
import { AuthorPopup } from "@/components/AuthorPopup";
import { AwardBadge } from "@/components/AwardBadge";

// Script/comic data interface for original screenplays and comics
interface ScriptData {
  /** sf_film_adaptations.id — required to read a screenplay inline, because the
      edge function resolves the URL from our own row rather than trusting one
      sent by the browser. */
  id?: string | null;
  film_title: string;
  film_year: number | null;
  director: string | null;
  book_author: string; // screenwriters or comic artist
  poster_url: string | null;
  book_cover_url: string | null;
  script_url: string | null;
  script_source: string | null;
  notable_differences?: string | null;
  adaptation_type?: string | null; // 'original', 'comic', etc.
}

interface EnhancedBookPreviewModalProps {
  book: EnrichedPublisherBook;
  onClose: () => void;
  /**
   * Adds the book to the viewer's signals. OPTIONAL, and the "Add Signal"
   * button only renders when it is supplied.
   *
   * It used to be required, so surfaces with nothing sensible to do passed
   * `() => {}` — and the button still rendered, still looked live, and
   * silently discarded every click. Six call sites had done this, including
   * a followed reader's library on /community. Making the prop optional means
   * the absence of a handler removes the button instead of deadening it.
   */
  onAddBook?: (book: EnrichedPublisherBook) => void;
  // Optional script mode for original screenplays
  scriptData?: ScriptData | null;
}

const EnhancedBookPreviewModal = ({ book, onClose, onAddBook, scriptData }: EnhancedBookPreviewModalProps) => {
  const [appleBook, setAppleBook] = useState<AppleBook | null>(null);
  const [googleFallback, setGoogleFallback] = useState<any>(null);
  const [freeEbooks, setFreeEbooks] = useState<EbookSearchResult | null>(null);
  const [loading, setLoading] = useState(!scriptData); // Don't load for scripts
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCoverLightbox, setShowCoverLightbox] = useState(false);
  const [showComicReader, setShowComicReader] = useState(false);
  const { toast } = useEnhancedToast();
  const digitalCopyButtonRef = useRef<HTMLButtonElement>(null);
  
  // Script or comic mode
  const isScriptMode = !!scriptData;
  const isComicMode = scriptData?.adaptation_type === 'comic';

  // A book with no stored cover gets one from the verified rule (the same
  // rule as the nightly job, cached server-side) -- never from this modal's
  // live Apple or Google search, which matched title and author only and so
  // could show a Polish or French edition.
  const [verifiedCover, setVerifiedCover] = useState<string | null>(null);
  useEffect(() => {
    setVerifiedCover(null);
    if (isScriptMode || book.cover_url || !book.title || !book.author) return;
    let cancelled = false;
    fetchVerifiedCovers([{ title: book.title, author: book.author }]).then((found) => {
      if (!cancelled) setVerifiedCover(found.get(coverCacheKey(book.title, book.author)) ?? null);
    });
    return () => { cancelled = true; };
  }, [book.title, book.author, book.cover_url, isScriptMode]);

  // A screenplay we can show in our own reader: an imsdb script, and we know
  // which row it is (the fetch is keyed on the id, never on a supplied URL).
  const isInlineReadableScript =
    !isComicMode &&
    !!scriptData?.id &&
    !!scriptData?.script_url &&
    /imsdb\.com\/scripts\//i.test(scriptData.script_url);

  // Script writer popup state
  const [selectedAuthor, setSelectedAuthor] = useState<ScifiAuthor | null>(null);
  const [showAuthorPopup, setShowAuthorPopup] = useState(false);

  // Parse writers from book_author field (e.g., "Fred Dekker & Shane Black")
  const parseWriters = (authorString: string): string[] => {
    if (!authorString || authorString === 'Unknown Screenwriter') return [];
    return authorString
      .split(/\s*[&,]\s*|\s+and\s+/i)
      .map(name => name.trim())
      .filter(name => name.length > 0 && name !== 'Unknown Screenwriter');
  };

  const writers = scriptData ? parseWriters(scriptData.book_author) : [];

  // Handle writer click - open AuthorPopup
  const handleWriterClick = async (writerName: string) => {
    const { data } = await supabase
      .from('scifi_authors')
      .select('*')
      .ilike('name', `%${writerName}%`)
      .limit(1)
      .single();

    if (data) {
      setSelectedAuthor(data as ScifiAuthor);
    } else {
      setSelectedAuthor({
        id: 'temp',
        name: writerName,
        created_at: '',
        updated_at: '',
      } as ScifiAuthor);
    }
    setShowAuthorPopup(true);
  };

  // Get script URL - prefer direct PDF if available
  const getScriptUrl = (): string | null => {
    if (!scriptData?.script_url) return null;
    
    // If it's already a PDF URL, use it directly
    if (scriptData.script_url.endsWith('.pdf') || scriptData.script_url.includes('/pdf/')) {
      return scriptData.script_url;
    }
    
    // If it's a ScriptSlug page URL, try to construct direct PDF link
    if (scriptData.script_url.includes('scriptslug.com/script/')) {
      const scriptPath = scriptData.script_url.split('/script/')[1];
      if (scriptPath) {
        return `https://assets.scriptslug.com/live/pdf/scripts/${scriptPath}.pdf`;
      }
    }
    
    return scriptData.script_url;
  };

  const scriptUrl = getScriptUrl();
  const scriptSourceLabel = isComicMode ? 'Comic Book' :
                           scriptData?.script_source === 'scriptslug' ? 'ScriptSlug' : 
                           scriptData?.script_source === 'imsdb' ? 'IMSDb' : 
                           scriptData?.script_source === 'Comic Book' ? 'Comic Book' : 'Script';

  // GSAP hover animations for digital copy button
  useEffect(() => {
    const button = digitalCopyButtonRef.current;
    if (button) {
      const handleMouseEnter = () => {
        gsap.to(button, {
          borderColor: "#89b4fa",
          boxShadow: "0 0 8px #89b4fa66",
          duration: 0.3,
          ease: "power2.inOut"
        });
      };

      const handleMouseLeave = () => {
        gsap.to(button, {
          borderColor: "rgba(255, 255, 255, 0.15)",
          boxShadow: "0 0 0px transparent",
          duration: 0.3,
          ease: "power2.inOut"
        });
      };

      button.addEventListener('mouseenter', handleMouseEnter);
      button.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        button.removeEventListener('mouseenter', handleMouseEnter);
        button.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [appleBook, freeEbooks]);

  useEffect(() => {
    // Skip API calls for script mode - scripts don't need book lookups
    if (isScriptMode) {
      setLoading(false);
      return;
    }
    
    const fetchBookData = async () => {
      const startTime = Date.now();
      setLoading(true);
      setError(null);

      console.log('🔍 [BookPreview] Starting fetch for:', {
        title: book.title,
        author: book.author,
        isbn: book.isbn,
        timestamp: new Date().toISOString()
      });

      // Log preview interaction — fire-and-forget; it must never delay the lookups.
      void analyticsService.logBookPreview(
        { title: book.title, author: book.author, isbn: book.isbn },
        'publisher_resonance'
      );

      try {
        const settings = getOptimizedSettings();
        const searchResults = { apple: false, freeEbooks: false, google: false };
        
        // Launch Apple Books and Free Ebooks in parallel to avoid gating
        const appleStartTime = Date.now();
        const freeEbookStartTime = Date.now();
        
        const applePromise = searchAppleBooks(book.title, book.author, book.isbn);
        const freePromise = (async () => {
          // Use the cache: forcing a refresh made every open pay full network cost.
          const freeEbookPromise = searchFreeEbooks(book.title, book.author, book.isbn);

          const timeoutDuration = Math.max(settings.maxConcurrentRequests * 1000, 15000); // Min 15s timeout
          const timeoutPromise = new Promise<EbookSearchResult | null>(resolve => 
            setTimeout(() => {
              console.log(`⏰ [BookPreview] Free ebook search timed out after ${timeoutDuration}ms`);
              resolve(null);
            }, timeoutDuration)
          );
          return Promise.race([freeEbookPromise, timeoutPromise]);
        })();
        
        const [appleResult, freeEbookResult] = await Promise.allSettled([applePromise, freePromise]).then((results) => {
          const apple = results[0].status === 'fulfilled' ? (results[0].value as AppleBook | null) : null;
          const free = results[1].status === 'fulfilled' ? (results[1].value as EbookSearchResult | null) : null;
          return [apple, free] as const;
        });
        
        const appleResponseTime = Date.now() - appleStartTime;
        const freeEbookResponseTime = Date.now() - freeEbookStartTime;
        
        if (appleResult) {
          console.log(`✅ [BookPreview] Apple Books result found in ${appleResponseTime}ms:`, {
            title: appleResult.title,
            price: appleResult.formattedPrice,
            hasDescription: !!appleResult.description,
            coverUrl: !!appleResult.coverUrl
          });
          setAppleBook(appleResult);
          searchResults.apple = true;
          await analyticsService.logApiResponse('apple_books', appleResponseTime, true);
        } else {
          console.log(`❌ [BookPreview] No Apple Books result after ${appleResponseTime}ms`);
          await analyticsService.logApiResponse('apple_books', appleResponseTime, false);
        }
        
        if (freeEbookResult) {
          console.log(`📚 [BookPreview] Free ebook search completed in ${freeEbookResponseTime}ms:`, {
            hasLinks: !!freeEbookResult?.hasLinks,
            hasGutenberg: !!freeEbookResult?.gutenberg,
            hasArchive: !!freeEbookResult?.archive,
            timedOut: freeEbookResult === null
          });
          setFreeEbooks(freeEbookResult);
          searchResults.freeEbooks = !!freeEbookResult?.hasLinks;
          await analyticsService.logApiResponse('free_ebooks', freeEbookResponseTime, !!freeEbookResult);
        }
        
        // Fallback to Google Books only if nothing else found
        if (!appleResult && !freeEbookResult?.hasLinks) {
          console.log('📖 [BookPreview] No Apple or free ebooks found, trying Google Books fallback...');
          const googleStartTime = Date.now();
          const googleBooks = await searchGoogleBooks(`${book.title} ${book.author}`, 1);
          const googleResponseTime = Date.now() - googleStartTime;
          
          if (googleBooks.length > 0) {
            console.log(`✅ [BookPreview] Google Books fallback result found in ${googleResponseTime}ms:`, {
              title: googleBooks[0].title,
              hasDescription: !!googleBooks[0].description,
              coverUrl: !!googleBooks[0].coverUrl
            });
            setGoogleFallback(googleBooks[0]);
            searchResults.google = true;
          } else {
            console.log(`❌ [BookPreview] No Google Books result after ${googleResponseTime}ms`);
          }
          
          await analyticsService.logApiResponse('google_books', googleResponseTime, googleBooks.length > 0);
        }
        
        const totalResponseTime = Date.now() - startTime;
        console.log(`🎯 [BookPreview] Search summary for "${book.title}" (${totalResponseTime}ms):`, searchResults);
        
        // Log search patterns for analytics
        await analyticsService.logSearchPattern(
          { title: book.title, author: book.author, isbn: book.isbn },
          {
            isNonEnglish: !/^[a-zA-Z0-9\s.,!?'-]+$/.test(book.title),
            hasSpecialChars: /[^\w\s.,!?'-]/.test(book.title),
            titleLength: book.title.length,
            priority: searchResults.apple ? 'high' : searchResults.freeEbooks ? 'normal' : 'low'
          },
          'publisher_resonance'
        );
        
        // Log digital library success/failure
        await analyticsService.logDigitalLibrarySuccess(
          { title: book.title, author: book.author, isbn: book.isbn },
          {
            apple: searchResults.apple,
            gutenberg: !!freeEbooks?.gutenberg,
            archive: !!freeEbooks?.archive,
            google: searchResults.google
          },
          'publisher_resonance'
        );
        
        // Log "No Signal Detected" cases for pattern analysis
        if (!searchResults.apple && !searchResults.freeEbooks && !searchResults.google) {
          await analyticsService.logNoSignalDetected(
            { title: book.title, author: book.author, isbn: book.isbn },
            { apple: true, freeEbooks: true, google: true }, // All attempts made
            'publisher_resonance'
          );
        }
      } catch (err) {
        console.error('💥 Error fetching book data:', err);
        setError('Failed to load book preview data');
        
        const responseTime = Date.now() - startTime;
        await analyticsService.logApiResponse('book_preview', responseTime, false);
        
        // Log error interaction
        await analyticsService.logBookInteraction({
          book_title: book.title,
          book_author: book.author,
          book_isbn: book.isbn,
          interaction_type: 'preview',
          source_context: 'publisher_resonance',
          success: false,
          error_details: err instanceof Error ? err.message : 'Unknown error',
          response_time_ms: responseTime
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookData();
  }, [book.title, book.author, book.isbn, isScriptMode]);

  const isSameBook = (externalTitle: string | undefined, externalAuthor: string | undefined): boolean =>
    !!externalTitle && titleMatches(book.title, externalTitle) &&
    (!externalAuthor || authorMatches(book.author, [externalAuthor]));

  const handleDigitalCopyAction = () => {
    // Same rule as the cover below (isSameBook): the Buy button and the cover
    // used to have separate matchers that disagreed, so one could accept a
    // result the other rejected.
    const isValidMatchAction = isSameBook;

    // Apple Books - only if validated
    const isAppleValid = appleBook && isValidMatchAction(appleBook.title, appleBook.author);
    if (isAppleValid) {
      // Open link FIRST (synchronously) to preserve user gesture for Safari
      try {
        const isAppleDevice = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
        
        if (isAppleDevice) {
          const deepLink = generateAppleBooksDeepLink(appleBook.id);
          window.location.href = deepLink;
          
          toast({
            title: "Opening Apple Books",
            description: "Launching Apple Books app...",
          });
        } else {
          const webUrl = generateAppleBooksWebUrl(appleBook.id);
          window.open(webUrl, '_blank', 'noopener,noreferrer');
          
          toast({
            title: "Opening Apple Books",
            description: "Opening in web browser...",
          });
        }
        
        // Log analytics AFTER opening (non-blocking)
        analyticsService.logDigitalCopyClick(
          { title: book.title, author: book.author, isbn: book.isbn },
          'apple_books',
          'publisher_resonance'
        );
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to open Apple Books. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // Whichever source the reader can actually read — Gutenberg when we have
    // it. These two branches used to be ordered Archive-first, so a book with
    // a public-domain Gutenberg edition still sent the reader to a borrow-only
    // Archive page. See preferredEbookSource().
    const preferred = preferredEbookSource(freeEbooks);

    if (preferred?.kind === 'archive') {
      // Open link FIRST to preserve user gesture for Safari mobile
      window.location.href = preferred.url;

      toast({
        title: "Opening Digital Library",
        description: "Redirecting to digital library...",
      });

      // Log analytics AFTER (non-blocking)
      analyticsService.logDigitalCopyClick(
        { title: book.title, author: book.author, isbn: book.isbn },
        'internet_archive',
        'publisher_resonance'
      );
      return;
    }

    if (preferred?.kind === 'gutenberg') {
      // Open link FIRST to preserve user gesture for Safari mobile
      window.location.href = preferred.url;

      toast({
        title: "Opening Digital Library",
        description: "Redirecting to digital library...",
      });

      // Log analytics AFTER (non-blocking)
      analyticsService.logDigitalCopyClick(
        { title: book.title, author: book.author, isbn: book.isbn },
        'project_gutenberg',
        'publisher_resonance'
      );
      return;
    }
  };

  // Is an Apple/Google result THIS book? The shared rule the nightly cover job
  // also uses (supabase/functions/_shared/bookCover.ts): same title once
  // articles, punctuation and subtitles are ignored, and the same author
  // surname. The old local check accepted any substring, so "Foundation"
  // matched "Foundation and Empire" and took its cover.
  const isValidMatch = isSameBook;

  // Validate external results before using their cover images
  const validAppleBook = appleBook && isValidMatch(appleBook.title, appleBook.author) ? appleBook : null;
  const validGoogleBook = googleFallback && isValidMatch(googleFallback.title, googleFallback.author) ? googleFallback : null;

  // For scripts/comics, use script data; for books, use API results or fallback
  const displayData = isScriptMode 
    ? { 
        title: isComicMode ? book.title : scriptData!.film_title, 
        author: scriptData!.book_author 
      }
    : (validAppleBook || validGoogleBook || book);
  
  // Scripts show a neutral icon; comics their cover. Books: the stored cover,
  // else the verified one, else the Google volume's own cover when the reader
  // opened a Google search result (that IS the edition they picked).
  const coverUrl = isScriptMode 
    ? (isComicMode ? (book.cover_url || scriptData?.book_cover_url) : null)
    : (book.cover_url || verifiedCover || book.google_cover_url);
  
  // Description - use validated API results or editorial note (not for scripts)
  const description = isScriptMode 
    ? scriptData?.notable_differences 
    : (validAppleBook?.description || validGoogleBook?.description || book.editorial_note);

  // Determine which digital copy option to show (use validated Apple result only)
  const getDigitalCopyInfo = () => {
    if (validAppleBook) {
      return {
        service: 'Apple Books',
        hasPrice: true,
        price: validAppleBook.formattedPrice || 
               (validAppleBook.price === 0 ? 'Free' : 
                `${validAppleBook.currency || '£'}${validAppleBook.price || 'N/A'}`),
        buttonText: 'Buy',
        disabled: false
      };
    }
    
    // Name the source the Read button will actually open. These were two
    // branches ordered Archive-first, which disagreed with nothing at the time
    // — but the click handler had the same rule written out separately, and
    // once one was corrected the label would have started lying.
    const preferredSource = preferredEbookSource(freeEbooks);
    if (preferredSource) {
      return {
        service: preferredSource.label,
        hasPrice: false,
        buttonText: 'Read',
        disabled: false
      };
    }

    return {
      service: 'Digital Libraries',
      hasPrice: false,
      buttonText: 'No Signal Detected',
      disabled: true
    };
  };

  const digitalCopyInfo = getDigitalCopyInfo();
  const hasDigitalCopy = !!(validAppleBook || freeEbooks?.archive?.url || freeEbooks?.gutenberg?.url);

  // Swipe-to-dismiss state
  const modalContentRef = useRef<HTMLDivElement>(null);
  const swipeStartY = useRef<number | null>(null);
  const swipeCurrentY = useRef<number>(0);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollableEl = modalContentRef.current?.querySelector('.flex-1.overflow-y-auto');
    // Only start swipe if scrolled to top
    if (scrollableEl && scrollableEl.scrollTop > 0) return;
    swipeStartY.current = e.touches[0].clientY;
    swipeCurrentY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (swipeStartY.current === null) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - swipeStartY.current;
    if (diff > 0) {
      setSwipeOffset(diff);
      swipeCurrentY.current = currentY;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (swipeOffset > 120) {
      // Dismiss
      gsap.to(modalContentRef.current, {
        y: window.innerHeight,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: onClose,
      });
    } else if (swipeOffset > 0) {
      // Spring back
      setSwipeOffset(0);
      if (modalContentRef.current) {
        gsap.fromTo(modalContentRef.current, { y: swipeOffset }, {
          y: 0,
          duration: 0.4,
          ease: 'elastic.out(1, 0.5)',
        });
      }
    }
    swipeStartY.current = null;
  }, [swipeOffset, onClose]);

  const modal = (
    <div className="fixed inset-0 z-[2000] flex h-[100dvh] w-screen items-center justify-center bg-background/50 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={modalContentRef}
        className="modal-content bg-slate-800/50 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col"
        style={{ transform: swipeOffset > 0 ? `translateY(${swipeOffset}px)` : undefined, opacity: swipeOffset > 0 ? Math.max(0.3, 1 - swipeOffset / 300) : 1 }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Swipe indicator */}
        <div className="flex justify-center pt-2 pb-0 flex-shrink-0">
          <div className="w-10 h-1 bg-slate-500/50 rounded-full" />
        </div>
        {/* Header */}
        <div className="p-3 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 ${isComicMode ? 'bg-amber-400' : isScriptMode ? 'bg-cyan-400' : 'bg-blue-400'} rounded-full`}></div>
              <span className="text-slate-200 text-base font-medium">
                {isComicMode ? 'Biographical Comic' : isScriptMode ? 'Original Screenplay' : 'Signal Preview'}
              </span>
            </div>
            <button
              onClick={onClose}
              aria-label="Close preview"
              className="text-slate-400 hover:text-slate-200 transition-colors p-1 rounded hover:bg-slate-700/50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide pt-4 px-6 pb-6">
          {error ? (

            <div className="text-center py-12">
              <p className="text-red-400 mb-4">{error}</p>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {/* Cover + Info */}
              <div className="flex space-x-4">
                {/* Cover — tap to enlarge (cover art is worth seeing properly) */}
                <div className="flex-shrink-0 w-24 h-36 bg-slate-700/50 border border-slate-600 rounded-lg overflow-hidden relative shadow-lg">
                  {isScriptMode && !isComicMode ? (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                      <FileText className="w-8 h-8 text-cyan-400/50" />
                    </div>
                  ) : (
                    <EnhancedBookCover
                      title={displayData.title}
                      author={displayData.author}
                      isbn={book.isbn}
                      coverUrl={coverUrl}
                      className="w-24 h-36"
                    />
                  )}
                  {coverUrl && (
                    <button
                      onClick={() => setShowCoverLightbox(true)}
                      aria-label={`Enlarge cover of ${displayData.title}`}
                      className="group absolute inset-0 flex items-end justify-end p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 rounded-lg"
                    >
                      <span className="flex items-center justify-center w-6 h-6 rounded-md bg-slate-900/75 border border-slate-600/80 text-slate-300 backdrop-blur-sm transition-colors group-hover:text-cyan-300 group-hover:border-cyan-400/50">
                        <Expand className="w-3.5 h-3.5" />
                      </span>
                    </button>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 space-y-2">
                  <div>
                    <h2 className="text-slate-200 font-bold text-xl leading-tight">
                      {displayData.title}
                    </h2>
                    {!isScriptMode && (
                      <AwardBadge 
                        title={displayData.title} 
                        author={displayData.author} 
                        size="md" 
                        showAll 
                        className="mt-2"
                      />
                    )}
                    {isScriptMode ? (
                      <>
                        <Badge variant="outline" className="mt-1 text-cyan-400 border-cyan-400/30 bg-cyan-400/10 text-xs">
                          Original Screenplay
                        </Badge>
                        {scriptData?.film_year && (
                          <p className="text-muted-foreground text-sm mt-1">{scriptData.film_year}</p>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => handleWriterClick(displayData.author)}
                        className="text-slate-400 text-base font-medium hover:text-emerald-400 transition-colors cursor-pointer text-left"
                      >
                        {displayData.author}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Script Mode: Writers Section */}
              {isScriptMode && writers.length > 0 && (
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-300">Written by</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {writers.map((writer, index) => (
                      <button
                        key={index}
                        onClick={() => handleWriterClick(writer)}
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 hover:border-emerald-400/50 rounded-lg text-sm text-emerald-300 hover:text-emerald-200 transition-all flex items-center gap-1.5"
                      >
                        <User className="w-3 h-3" />
                        {writer}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Script Mode: Director */}
              {isScriptMode && scriptData?.director && (
                <div className="text-sm text-muted-foreground">
                  <span className="text-amber-400">Director:</span> {scriptData.director}
                </div>
              )}

              {/* Script/Comic Mode: Read Button */}
              {isScriptMode && scriptUrl && (
                isComicMode ? (
                  <button
                    onClick={() => setShowComicReader(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <BookOpen className="w-5 h-5" />
                    Read Comic Book
                  </button>
                ) : isInlineReadableScript ? (
                  /* imsdb scripts now open in our own reader instead of
                     ejecting the reader to a third-party page. The reader still
                     falls back to an outbound link if the fetch fails, so this
                     never becomes a dead end. */
                  <button
                    onClick={() => setShowComicReader(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <FileText className="w-5 h-5" />
                    Read Screenplay
                  </button>
                ) : (
                  <a
                    href={scriptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <FileText className="w-5 h-5" />
                    Read Script on {scriptSourceLabel}
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                )
              )}

              {/* Script Mode: No script available */}
              {isScriptMode && !scriptUrl && (
                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/50 text-center">
                  <FileText className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {isComicMode ? 'Comic not yet available in our database' : 'Script not yet available in our database'}
                  </p>
                </div>
              )}

              {/* Book Mode: Synopsis */}
              {!isScriptMode && description && (
                <div className="space-y-2">
                  <h3 className="text-slate-200 font-semibold text-base">Synopsis</h3>
                  <div className="text-slate-400 text-sm leading-relaxed max-h-32 overflow-y-auto scrollbar-hide">
                    {description.split('\n').map((paragraph, index) => (
                      <p key={index} className={index > 0 ? 'mt-2' : ''}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Book Mode: Publisher Hard Copy Section */}
              {!isScriptMode && book.publisher_link && (
                <div className="border border-slate-700 rounded-lg p-2 bg-slate-700/20 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-200 text-sm font-medium">Publisher Store</span>
                        <span className="text-slate-400 text-sm">(Hard Copy)</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (book.publisher_link) {
                          window.open(book.publisher_link, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      className="h-9 px-3 py-1.5 bg-transparent border border-[rgba(255,255,255,0.15)] text-xs rounded-lg transition-all duration-300 ease-in-out text-[#cdd6f4] hover:border-[#89b4fa]"
                      style={{
                        boxShadow: "0 0 0px transparent"
                      }}
                    >
                      Visit Store
                    </button>
                  </div>
                </div>
              )}

              {/* Book Mode: Digital Copy Section — the only part that waits on
                  the network, so the spinner lives here rather than gating the
                  whole card. */}
              {!isScriptMode && (
                <div className="border border-slate-700 rounded-lg p-2 bg-slate-700/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-4 h-4 text-slate-400" />
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-200 text-sm font-medium">
                          {loading ? 'Scanning for digital copies' : digitalCopyInfo.service}
                        </span>
                        {!loading && digitalCopyInfo.hasPrice && (
                          <span className="text-slate-400 text-sm">{digitalCopyInfo.price}</span>
                        )}
                      </div>
                    </div>
                    {loading ? (
                      <div className="w-4 h-4 mr-3 border-2 border-slate-600/40 border-t-blue-400 rounded-full animate-spin" />
                    ) : (
                      <button
                        ref={digitalCopyButtonRef}
                        onClick={digitalCopyInfo.disabled ? undefined : handleDigitalCopyAction}
                        disabled={digitalCopyInfo.disabled}
                        className={`h-9 px-3 py-1.5 bg-transparent border border-[rgba(255,255,255,0.15)] text-xs rounded-lg transition-all duration-300 ease-in-out ${
                          digitalCopyInfo.disabled 
                            ? 'text-slate-500 cursor-not-allowed' 
                            : 'text-[#cdd6f4] hover:border-[#89b4fa]'
                        }`}
                        style={{
                          boxShadow: "0 0 0px transparent"
                        }}
                      >
                        {digitalCopyInfo.buttonText}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
        
        {/* Actions */}
        <div className="flex justify-between space-x-3 p-4 border-t border-slate-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 h-9 px-3 py-1.5 bg-transparent border border-[rgba(255,255,255,0.15)] text-[#cdd6f4] text-xs rounded-lg transition-all duration-300 ease-in-out hover:border-[#89b4fa]"
            style={{
              boxShadow: "0 0 0px transparent"
            }}
          >
            Close
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            className="h-9 px-3 py-1.5 bg-transparent border border-[rgba(255,255,255,0.15)] text-[#cdd6f4] text-xs rounded-lg transition-all duration-300 ease-in-out hover:border-[#89b4fa] flex items-center justify-center"
            style={{
              boxShadow: "0 0 0px transparent"
            }}
          >
            <Share2 className="w-3 h-3 mr-1" />
            Share
          </button>
          {onAddBook && (
            <button
              onClick={() => {
                // Fire-and-forget: adding should feel instant.
                void analyticsService.logBookAdd(
                  { title: book.title, author: book.author, isbn: book.isbn },
                  'publisher_resonance'
                );
                onAddBook(book);
                onClose();
              }}

              className="flex-1 h-9 px-3 py-1.5 bg-transparent border border-[rgba(255,255,255,0.15)] text-[#cdd6f4] text-xs rounded-lg transition-all duration-300 ease-in-out hover:border-[#89b4fa] flex items-center justify-center"
              style={{
                boxShadow: "0 0 0px transparent"
              }}
            >
              <Plus className="w-3 h-3 mr-2" />
              Add Signal
            </button>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareBookModal
          book={{
            title: book.title,
            author: book.author,
            cover_url: coverUrl,
          }}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Enlarged cover */}
      {showCoverLightbox && coverUrl && (
        <CoverLightbox
          src={coverUrl}
          title={displayData.title}
          author={displayData.author || book.author}
          isbn={book.isbn || undefined}
          onClose={() => setShowCoverLightbox(false)}
        />
      )}

      {/* Author Popup */}
      {selectedAuthor && (
        <AuthorPopup
          author={selectedAuthor}
          isVisible={showAuthorPopup}
          onClose={() => {
            setShowAuthorPopup(false);
            setSelectedAuthor(null);
          }}
        />
      )}

      {/* Comic Reader Modal - Rendered outside this modal's DOM via portal */}
      {showComicReader && scriptData && scriptData.script_url && (
        <ScreenplayReaderModal
          screenplay={{
            id: scriptData.id,
            film_title: scriptData.film_title,
            script_url: scriptData.script_url,
            /* script_source was hardcoded to 'Comic Book', which was harmless
               while a comic was the only thing this reader ever opened. Now
               that screenplays open here too, hardcoding it would send every
               one of them down the comic branch and render page-N.webp URLs
               against a script. Pass the real value, keeping the old constant
               for comics so their behaviour is untouched. */
            script_source: isComicMode ? 'Comic Book' : scriptData.script_source,
            poster_url: scriptData.book_cover_url,
            director: scriptData.director,
            film_year: scriptData.film_year,
          }}
          isOpen={showComicReader}
          onClose={() => setShowComicReader(false)}
        />
      )}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);

};

export default EnhancedBookPreviewModal;